// src/components/layout/Layout.tsx
import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { showToast } from '../common/Toast';

import Header from '../common/Header';
import CharacterCreateWizard from '../character/CharacterCreateWizard';
import LoginModal from '../auth/LoginModal';
import SignupModal from '../auth/SignupModal';
import logoImageWhite from '../../assets/images/UI_orifoli_logo_white.png';
import poweredByFigryne from '../../assets/images/UI_powered_by_figryne_white.png';
import Toast from '../common/Toast';
import { config } from '../../config';

const Layout: React.FC = () => {
  const { t } = useTranslation('footer');
  const location = useLocation();
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [wizardDefaultCategory, setWizardDefaultCategory] = useState<'original' | 'vtuber'>('original');
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [openWizardAfterAuth, setOpenWizardAfterAuth] = useState(false);

  // モーダル切り替えハンドラ
  const handleSwitchToSignup = () => {
    setIsLoginModalOpen(false);
    setIsSignupModalOpen(true);
  };

  const handleSwitchToLogin = () => {
    setIsSignupModalOpen(false);
    setIsLoginModalOpen(true);
  };

  // カスタムイベントでモーダルを開く（子ページから呼び出し可能）
  useEffect(() => {
    const handleOpenLogin = () => setIsLoginModalOpen(true);
    const handleOpenSignup = () => setIsSignupModalOpen(true);
    const handleOpenCreate = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setWizardDefaultCategory(detail?.defaultCategory || 'original');
      setIsWizardOpen(true);
    };
    const handleSignupThenCreate = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const category = detail?.defaultCategory || 'original';
      const returnTo = detail?.returnTo || window.location.pathname;
      setWizardDefaultCategory(category);
      setOpenWizardAfterAuth(true);
      // SignupModalが新規ユーザーを/settingsに送る時に使うstate情報を保存
      sessionStorage.setItem('afterAuthAction', JSON.stringify({
        returnTo,
        openWizard: true,
        defaultCategory: category,
      }));
      setIsSignupModalOpen(true);
    };
    window.addEventListener('open-login-modal', handleOpenLogin);
    window.addEventListener('open-signup-modal', handleOpenSignup);
    window.addEventListener('open-create-modal', handleOpenCreate);
    window.addEventListener('open-create-wizard', handleOpenCreate);
    window.addEventListener('open-signup-then-create', handleSignupThenCreate);
    return () => {
      window.removeEventListener('open-login-modal', handleOpenLogin);
      window.removeEventListener('open-signup-modal', handleOpenSignup);
      window.removeEventListener('open-create-modal', handleOpenCreate);
      window.removeEventListener('open-create-wizard', handleOpenCreate);
      window.removeEventListener('open-signup-then-create', handleSignupThenCreate);
    };
  }, []);

  // ページ遷移時のトースト表示（sessionStorageにメッセージが保存されている場合）
  useEffect(() => {
    const message = sessionStorage.getItem('pendingToast');
    if (message) {
      sessionStorage.removeItem('pendingToast');
      setTimeout(() => {
        showToast(message);
      }, 600);
    }
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-[#f8f7f8] dark:bg-gray-900 transition-colors">
      {/* Header */}
      <Header
        onCreateClick={() => { setWizardDefaultCategory('original'); setIsWizardOpen(true); }}
        onLoginClick={() => setIsLoginModalOpen(true)}
        onSignupClick={() => setIsSignupModalOpen(true)}
      />

      {/* Toast */}
      <Toast />

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 bg-black dark:bg-gray-950 text-white">
        <div className="container mx-auto max-w-8xl">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img src={logoImageWhite} alt="ORIFOLI" className="h-6" />
              <p className="text-white text-sm font-semibold mt-4">
                {t('description')}
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">{t('company.title')}</h4>
              <ul className="space-y-2 text-gray-300 text-sm opacity-70">
                <li><a href={config.figryneUrl} className="hover:text-white hover:opacity-100">{t('company.figryne')}</a></li>
                <li><a href="https://www.mmzk.co.jp/" target="_blank" rel="noopener noreferrer" className="hover:text-white hover:opacity-100">{t('company.about')}</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">{t('legal.title')}</h4>
              <ul className="space-y-2 text-gray-300 text-sm opacity-70">
                <li><a href={`${config.figryneUrl}/terms`} target="_blank" rel="noopener noreferrer" className="hover:text-white hover:opacity-100">{t('legal.terms')}</a></li>
                <li><a href={`${config.figryneUrl}/privacy`} target="_blank" rel="noopener noreferrer" className="hover:text-white hover:opacity-100">{t('legal.privacy')}</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">{t('social.title')}</h4>
              <div className="flex space-x-4">
                <a href="https://twitter.com/figryne" className="opacity-70 hover:opacity-100">
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-4 border-t border-white border-opacity-20  text-gray-400 text-xs">
            {t('copyright', { year: new Date().getFullYear() })}
          </div>
          <div className="flex justify-end">
              <a href={config.figryneUrl} className="hover:opacity-80 transition-opacity">
                <img src={poweredByFigryne} alt={t('poweredBy')} className="h-5" />
              </a>
            </div>
        </div>
      </footer>

      {/* Character Create Wizard */}
      <CharacterCreateWizard
        isOpen={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
        defaultCategory={wizardDefaultCategory}
      />

      {/* Login Modal */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => { setIsLoginModalOpen(false); setOpenWizardAfterAuth(false); }}
        onSwitchToSignup={handleSwitchToSignup}
        onSuccess={openWizardAfterAuth ? () => {
          setOpenWizardAfterAuth(false);
          setIsWizardOpen(true);
        } : undefined}
      />

      {/* Signup Modal */}
      <SignupModal
        isOpen={isSignupModalOpen}
        onClose={() => { setIsSignupModalOpen(false); setOpenWizardAfterAuth(false); }}
        onSwitchToLogin={handleSwitchToLogin}
        onSuccess={openWizardAfterAuth ? () => {
          setOpenWizardAfterAuth(false);
          setIsWizardOpen(true);
        } : undefined}
      />
    </div>
  );
};

export default Layout;